// Globals
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeOrderStatus, completeOrder, deleteOrderFiles, selectOrder, selectOrdersIsLoading } from '../../feature';

// Components
import { Button } from '@/ui';
import { DocumentsDetails, StyledTabs } from '../../components';
import {
  StyledContainer,
  StyledDocumentsContainer,
  StyledDocumentsFooter,
  StyledFooter,
  StyledNoteMessage,
  StyledSubmitButton,
} from './DocumentsContainerStyles';

// Models
import { EntityType } from '@/models/logs.model';
import { OrderStatus } from '@/models/order.model';
import { FileType } from '@/models/uploads.model';
import { FileVariant } from '@/models/file.model';
import { ROUTES } from '@/models/router.model';

// Modules
import { LogsListContainer } from '@/modules/Logs';
import { useUploadOrderFiles } from '@/modules/Order';
import { selectDocumentsIsLoading, DocumentsManagement } from '@/modules/PropertyDocuments';

// Helpers
import { getEntityId, toNumber } from '@/utils';

const DocumentsContainer = () => {
  const { t } = useTranslation('order');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppState(selectOrdersIsLoading);
  const order = useAppState(selectOrder);
  const documentsLoading = useAppState(selectDocumentsIsLoading);
  const { uploadFiles } = useUploadOrderFiles();
  const propertyId = getEntityId(order?.property_id ?? '');

  const handleChangeStatus = (status: OrderStatus) =>
    dispatch(changeOrderStatus({ id: getEntityId(order?.PK ?? ''), status }));

  const handleCompleteOrder = () => dispatch(completeOrder({ id: getEntityId(order?.PK ?? '') }));

  const handleUploadFile = async (value: File[]) => {
    if (!value?.length) return;
    const files = value.map((file) => ({ file, type: FileType.FILE }));
    uploadFiles({ orderId: getEntityId(order?.PK ?? ''), files, type: FileVariant.DOCUMENT_PROPERTY });
  };

  const deleteOrderFile = (fileId: string) => {
    dispatch(
      deleteOrderFiles({
        orderId: getEntityId(order?.PK ?? ''),
        fileId,
      }),
    );
  };

  const goToProperty = () => navigate(`${ROUTES.PROPERTIES}?id=${propertyId}`);

  if (!order) return null;

  const canCompleteOrder =
    (!!order?.photos_inside?.length || !!order?.photos_outside?.length) && order.status === OrderStatus.IN_PROGRESS;

  return (
    <StyledTabs
      destroyInactiveTabPane
      items={[
        {
          key: 'details',
          label: t('details'),
          children: (
            <>
              <StyledContainer>
                <DocumentsDetails isLoading={isLoading} order={order} onChangeStatus={handleChangeStatus} />
              </StyledContainer>
              {canCompleteOrder && (
                <StyledFooter>
                  <Button type="primary" shape="round" onClick={handleCompleteOrder}>
                    {t('send_photos', {
                      count: toNumber(order?.photos_inside?.length) + toNumber(order?.photos_outside?.length),
                    })}
                  </Button>
                </StyledFooter>
              )}
              {(order?.status === OrderStatus.IN_PROGRESS || order?.status === OrderStatus.COMPLETED) && (
                <>
                  <StyledDocumentsContainer>
                    <DocumentsManagement
                      uploadHidden={order?.status !== OrderStatus.IN_PROGRESS}
                      disabled={order?.status !== OrderStatus.IN_PROGRESS}
                      filesList={order?.files ?? []}
                      onFileUpload={handleUploadFile}
                      onDocumentDelete={deleteOrderFile}
                    />
                  </StyledDocumentsContainer>
                  <StyledDocumentsFooter>
                    <StyledNoteMessage>
                      <Trans
                        ns="documents"
                        i18nKey="documents_upload_mark_on_property"
                        components={{ span: <span onClick={goToProperty} /> }}
                      />
                    </StyledNoteMessage>
                    {order?.status === OrderStatus.IN_PROGRESS && (
                      <StyledSubmitButton
                        onClick={handleCompleteOrder}
                        shape="round"
                        type={'primary'}
                        loading={documentsLoading}
                      >
                        {t('send_document_other')}
                      </StyledSubmitButton>
                    )}
                  </StyledDocumentsFooter>
                </>
              )}
            </>
          ),
        },
        {
          key: 'logs',
          label: t('logs'),
          children: <LogsListContainer id={order?.PK ?? ''} entityType={EntityType.ORDER} />,
        },
      ]}
    />
  );
};

export default DocumentsContainer;
